import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Search, Loader2, BadgeDollarSign, XCircle, CalendarClock, Clock, Mail, X, Activity, Lock, Unlock, Printer } from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TYPES = [
  { key: "all", label: "All", icon: Activity, color: "text-white" },
  { key: "sale", label: "Sales", icon: BadgeDollarSign, color: "text-emerald-400" },
  { key: "cancellation", label: "Cancellations", icon: XCircle, color: "text-[#C44536]" },
  { key: "postponement", label: "Postponements", icon: CalendarClock, color: "text-sky-400" },
  { key: "cutoff", label: "Cutoffs", icon: Clock, color: "text-amber-400" },
  { key: "email", label: "Emails", icon: Mail, color: "text-violet-300" },
];
const TYPE_META = Object.fromEntries(TYPES.map((t) => [t.key, t]));

const fmtTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

function BuggySlotControl() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/admin/buggy/closures").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const isClosed = (d, s) => (data.closures[d] || []).includes(s);
  const toggle = async (d, s) => {
    const key = `${d}|${s}`;
    setBusy(key);
    try {
      const r = await api.post("/admin/buggy/closures", { date: d, slot: s, closed: !isClosed(d, s) });
      setData((prev) => ({ ...prev, closures: r.data.closures }));
      toast.success(isClosed(d, s) ? "Slot reopened" : "Slot closed");
    } catch { toast.error("Could not update slot"); } finally { setBusy(""); }
  };
  const dateLabel = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }); } catch { return d; } };

  return (
    <div data-testid="buggy-slot-control" className="border border-amber-500/25 bg-amber-500/[0.03] mb-10">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-amber-500/15 bg-amber-500/[0.06]">
        <Clock size={15} className="text-amber-400" />
        <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-amber-200">Buggy time slots · open / close</h3>
        <span className="text-[11px] text-amber-200/50 ml-2 hidden sm:inline">Closed slots are hidden from customers & block new bookings</span>
      </div>
      <div className="p-5 overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.12em] text-gray-500">
              <th className="text-left font-medium py-2 pr-4">Day</th>
              {data.slots.map((s) => <th key={s} className="font-medium py-2 px-1 text-center">{s}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.dates.map((d) => (
              <tr key={d} className="border-t border-white/5">
                <td className="py-2.5 pr-4 text-gray-300 whitespace-nowrap">{dateLabel(d)}</td>
                {data.slots.map((s) => {
                  const closed = isClosed(d, s);
                  const key = `${d}|${s}`;
                  return (
                    <td key={s} className="py-2 px-1 text-center">
                      <button data-testid={`slot-toggle-${d}-${s}`} onClick={() => toggle(d, s)} disabled={busy === key}
                        title={closed ? "Closed — tap to reopen" : "Open — tap to close"}
                        className={`w-full inline-flex items-center justify-center gap-1 py-1.5 px-1 border text-[11px] font-bold uppercase tracking-[0.08em] transition-colors disabled:opacity-40 ${closed ? "bg-[#C44536]/15 border-[#C44536]/50 text-[#e07a6b] hover:bg-[#C44536]/25" : "border-emerald-500/30 text-emerald-400/80 hover:border-emerald-400/60"}`}>
                        {busy === key ? <Loader2 size={11} className="animate-spin" /> : closed ? <Lock size={11} /> : <Unlock size={11} />}
                        {closed ? "Closed" : "Open"}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const esc = (t) => String(t ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
const printEmail = (e) => {
  const m = e.meta || {};
  const w = window.open("", "_blank");
  if (!w) return;
  const rows = [["Sent (UTC)", e.ts], ["To", m.to], ["Guest", e.guest_name], ["Subject", m.subject || e.title],
    ["Booking", m.booking_id], ["Booked item", [m.booking_item, m.booking_date, m.booking_slot].filter(Boolean).join(" · ")],
    ["Sent by", m.sent_by], ["Record", m.recorded_from || "Logged by the system at send time"]].filter(([, v]) => v);
  w.document.write(`<html><head><title>${esc(m.subject || e.title)}</title></head><body style="font-family:Helvetica,Arial,sans-serif;max-width:720px;margin:24px auto;color:#111">
    <h2 style="font-size:16px;margin:0 0 12px">Immersion Festival — record of email sent</h2>
    <table style="font-size:12px;border-collapse:collapse;margin-bottom:18px">${rows.map(([k, v]) => `<tr><td style="padding:3px 12px 3px 0;color:#666">${esc(k)}</td><td style="padding:3px 0">${esc(v)}</td></tr>`).join("")}</table>
    <div style="border:1px solid #ccc;padding:16px">${m.body_html || `<pre style="white-space:pre-wrap;font-family:inherit">${esc(m.body_text)}</pre>`}</div>
    </body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 400);
};

function EventRow({ e, onOpenEmail }) {
  const meta = TYPE_META[e.type] || TYPE_META.all;
  const Icon = meta.icon;
  return (
    <div data-testid={`activity-row-${e.id}`} className="flex items-start gap-4 px-5 py-4 border-b border-white/5 hover:bg-white/[0.02] transition-colors">
      <div className={`shrink-0 mt-0.5 ${meta.color}`}><Icon size={18} /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] uppercase tracking-[0.14em] font-bold ${meta.color}`}>{meta.label.replace(/s$/, "")}</span>
          <span className="text-[11px] text-gray-600">·</span>
          <span className="text-[11px] text-gray-500">{e.actor}</span>
        </div>
        <p className="text-white text-sm mt-1 truncate">{e.title}</p>
        {e.summary && <p className="text-xs text-gray-400 mt-1 break-words">{e.summary}</p>}
        {e.type === "email" && (
          <button data-testid={`view-email-${e.id}`} onClick={() => onOpenEmail(e)} className="mt-2 text-[11px] uppercase tracking-[0.12em] text-violet-300 hover:text-violet-200 inline-flex items-center gap-1.5 border border-violet-400/30 hover:border-violet-400/60 px-2.5 py-1 transition-colors">
            <Mail size={12} /> Read email
          </button>
        )}
      </div>
      <div className="shrink-0 text-right">
        <p className="text-[11px] text-gray-500 whitespace-nowrap">{fmtTime(e.ts)}</p>
        {e.guest_name && <p className="text-[11px] text-gray-600 mt-1 max-w-[140px] truncate">{e.guest_name}</p>}
      </div>
    </div>
  );
}

export default function ActivityLog() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ events: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [emailOpen, setEmailOpen] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/activity-log", { params: { type, q: q || undefined } })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [type, q]);

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const exportCsv = () => {
    const url = `${API}/admin/activity-log/export.csv${type !== "all" ? `?type=${type}` : ""}`;
    fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("immersion_admin_token")}` } })
      .then((r) => r.blob())
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = "activity-log.csv";
        a.click();
      }).catch(() => toast.error("Export failed"));
  };

  return (
    <div data-testid="activity-log-page" className="min-h-screen bg-black text-white">
      <div className="border-b border-white/10 px-6 md:px-12 py-5 flex items-center gap-4">
        <Link to="/admin" data-testid="back-to-admin" className="text-gray-400 hover:text-white inline-flex items-center gap-2 text-sm"><ArrowLeft size={16} /> Dashboard</Link>
        <span className="font-display font-bold tracking-tight text-lg ml-2">Activity Log</span>
        <div className="ml-auto flex items-center gap-3">
          <button data-testid="export-activity-csv" onClick={exportCsv} className="border border-white/20 text-white hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-2.5 px-5 transition-colors inline-flex items-center gap-2"><Download size={14} /> CSV</button>
          <button data-testid="activity-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="text-gray-500 hover:text-white text-sm uppercase tracking-[0.15em]">Logout</button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        <BuggySlotControl />

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-5">
          {TYPES.map((t) => {
            const Icon = t.icon;
            const active = type === t.key;
            const n = t.key === "all" ? data.total : (data.counts[t.key] || 0);
            return (
              <button key={t.key} data-testid={`filter-${t.key}`} onClick={() => setType(t.key)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 border text-xs uppercase tracking-[0.12em] font-bold transition-colors ${active ? "bg-white text-black border-white" : "border-white/15 text-gray-300 hover:border-white/50"}`}>
                <Icon size={14} className={active ? "" : t.color} /> {t.label}
                <span className={`text-[10px] ${active ? "text-black/60" : "text-gray-500"}`}>{n}</span>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
          <input data-testid="activity-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by guest name, email or detail…"
            className="w-full bg-[#0C0C0E] border border-white/15 pl-11 pr-4 py-3 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-white/40" />
        </div>

        {/* List */}
        <div className="border border-white/10 bg-[#0A0A0C]">
          {loading ? (
            <div className="flex items-center gap-2 text-gray-500 px-5 py-10"><Loader2 size={18} className="animate-spin" /> Loading…</div>
          ) : data.events.length === 0 ? (
            <p className="px-5 py-12 text-center text-gray-600 text-sm">No activity recorded yet for this filter.</p>
          ) : (
            data.events.map((e) => <EventRow key={e.id} e={e} onOpenEmail={setEmailOpen} />)
          )}
        </div>
      </div>

      {/* Email viewer */}
      {emailOpen && (
        <div data-testid="email-modal" className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEmailOpen(null)}>
          <div className="bg-[#0C0C0E] border border-white/15 max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-start gap-3 px-6 py-4 border-b border-white/10">
              <Mail size={18} className="text-violet-300 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-semibold break-words">{emailOpen.meta?.subject || emailOpen.title}</p>
                <p className="text-[11px] text-gray-500 mt-1">To {emailOpen.meta?.to} · {fmtTime(emailOpen.ts)}</p>
                {emailOpen.meta?.booking_id && (
                  <p data-testid="email-booking-ref" className="text-[11px] text-gray-400 mt-1">
                    {emailOpen.guest_name} · {emailOpen.meta.booking_item} {emailOpen.meta.booking_date || ""} {emailOpen.meta.booking_slot || ""} · Booking {emailOpen.meta.booking_id}{emailOpen.meta.sent_by ? ` · sent by ${emailOpen.meta.sent_by}` : ""}
                  </p>
                )}
              </div>
              <button data-testid="print-email-btn" onClick={() => printEmail(emailOpen)} className="text-[11px] uppercase tracking-[0.12em] text-gray-300 hover:text-white border border-white/20 hover:border-white/50 px-2.5 py-1 inline-flex items-center gap-1.5"><Printer size={12} /> Print / PDF</button>
              <button data-testid="close-email-modal" onClick={() => setEmailOpen(null)} className="text-gray-500 hover:text-white"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto p-6">
              <pre data-testid="email-body" className="whitespace-pre-wrap break-words text-sm text-gray-300 font-sans leading-relaxed">{emailOpen.meta?.body_text || "(no content)"}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
